import React, { useState, useEffect, useCallback, useRef } from 'react';
import fetchWithRetry from '../utils/fetchWithRetry';

const API_BASE = process.env.REACT_APP_API_URL ||
  (window.location.port === '3000' ? `http://${window.location.hostname}:3001` : '');

const CALL_STATES = { idle: 'idle', connecting: 'connecting', active: 'active' };

const PRESETS = [
  { value: 'sales',    label: 'Sales',    callerRole: 'Agent',   receiverRole: 'Prospect' },
  { value: 'support',  label: 'Support',  callerRole: 'Agent',   receiverRole: 'Customer' },
  { value: 'custom',   label: 'Custom',   callerRole: '',        receiverRole: '' },
];

const DIALPAD = ['1','2','3','4','5','6','7','8','9','*','0','#'];
const DIALPAD_LETTERS = { 2:'ABC', 3:'DEF', 4:'GHI', 5:'JKL', 6:'MNO', 7:'PQRS', 8:'TUV', 9:'WXYZ' };

function fmtTime(s) {
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

export default function Dialer({
  device, deviceReady, activeCall, setActiveCall,
  token, playbooks, userPlaybooks, selectedPlaybook, onPlaybookChange,
}) {
  const [number, setNumber] = useState('');
  const [callState, setCallState] = useState(CALL_STATES.idle);
  const [statusText, setStatusText] = useState('Initializing...');
  const [inputDevices, setInputDevices] = useState([]);
  const [outputDevices, setOutputDevices] = useState([]);
  const [selectedInput, setSelectedInput] = useState('default');
  const [selectedOutput, setSelectedOutput] = useState('default');

  const [callType, setCallType] = useState('sales');
  const [callerRole, setCallerRole] = useState('Agent');
  const [receiverRole, setReceiverRole] = useState('Prospect');
  const [callContext, setCallContext] = useState('');
  const [hideTranscript, setHideTranscript] = useState(() => localStorage.getItem('hideTranscript') === 'true');

  const toggleHideTranscript = () => {
    setHideTranscript((prev) => {
      const next = !prev;
      localStorage.setItem('hideTranscript', String(next));
      return next;
    });
  };

  const [muted, setMuted] = useState(false);
  const [held, setHeld] = useState(false);
  const [holdLoading, setHoldLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [showDevices, setShowDevices] = useState(false);
  const timerRef = useRef(null);
  const callSidRef = useRef(null);
  const [recentCalls, setRecentCalls] = useState([]);

  const refreshDevices = useCallback(() => {
    if (!device?.audio) return;
    setInputDevices(Array.from(device.audio.availableInputDevices.values()));
    setOutputDevices(Array.from(device.audio.availableOutputDevices.values()));
  }, [device]);

  useEffect(() => {
    if (deviceReady) {
      setStatusText('Ready');
      refreshDevices();
      device?.audio?.on('deviceChange', refreshDevices);
      return () => device?.audio?.removeListener('deviceChange', refreshDevices);
    } else {
      setStatusText('Initializing...');
    }
  }, [deviceReady, device, refreshDevices]);

  useEffect(() => {
    if (!token) return;
    fetchWithRetry(`${API_BASE}/api/reports?page=1&limit=10`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data?.reports) {
          const seen = new Set();
          const calls = [];
          for (const r of data.reports) {
            const num = r.callerNumber;
            if (num && !seen.has(num)) {
              seen.add(num);
              calls.push({ number: num, date: r.startedAt, name: r.contactName || null });
            }
          }
          setRecentCalls(calls);
        }
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (activeCall && callState !== CALL_STATES.active) {
      setCallState(CALL_STATES.active);
      setStatusText(`In call: ${activeCall.parameters?.From || 'Incoming'}`);
      callSidRef.current = activeCall.parameters?.CallSid || null;
      setElapsed(0);
      setMuted(false);
      setHeld(false);
      const onDisconnect = () => { setCallState(CALL_STATES.idle); setStatusText('Ready'); setActiveCall(null); callSidRef.current = null; };
      activeCall.on('disconnect', onDisconnect);
      activeCall.on('cancel', onDisconnect);
      return () => { activeCall.removeListener('disconnect', onDisconnect); activeCall.removeListener('cancel', onDisconnect); };
    } else if (!activeCall && callState === CALL_STATES.active) {
      setCallState(CALL_STATES.idle);
      setStatusText('Ready');
      callSidRef.current = null;
    }
  }, [activeCall, setActiveCall, callState]);

  useEffect(() => {
    if (callState === CALL_STATES.active) {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      return () => clearInterval(timerRef.current);
    }
    if (timerRef.current) clearInterval(timerRef.current);
  }, [callState]);

  const pickPreset = (v) => {
    setCallType(v);
    const p = PRESETS.find((x) => x.value === v);
    if (p) { setCallerRole(p.callerRole); setReceiverRole(p.receiverRole); }
  };

  const padPress = (k) => { setNumber((p) => p + k); if (activeCall) activeCall.sendDigits(k); };

  const handleDial = async () => {
    if (!device || !number.trim()) return;
    const to = number.startsWith('+') ? number.trim() : `+1${number.trim()}`;
    setCallState(CALL_STATES.connecting);
    setStatusText('Connecting...');
    setMuted(false);
    setHeld(false);
    try {
      const call = await device.connect({
        params: { To: to, callerRole: callerRole || 'Agent', receiverRole: receiverRole || 'Customer', callType: callType || 'sales', callContext: callContext || '', hideTranscript: hideTranscript ? 'true' : '' },
      });
      const reset = () => { setCallState(CALL_STATES.idle); setStatusText('Ready'); setActiveCall(null); callSidRef.current = null; };
      call.on('accept', () => { setCallState(CALL_STATES.active); setStatusText(`In call: ${to}`); setActiveCall(call); callSidRef.current = call.parameters?.CallSid || null; });
      call.on('disconnect', reset);
      call.on('cancel', reset);
      call.on('error', (err) => { console.error('Call error:', err); reset(); });
    } catch (err) {
      console.error('Dial failed:', err);
      setCallState(CALL_STATES.idle);
      setStatusText('Ready');
    }
  };

  const handleHangup = () => { activeCall?.disconnect(); setCallState(CALL_STATES.idle); setStatusText('Ready'); setActiveCall(null); callSidRef.current = null; };
  const toggleMute = () => { if (!activeCall) return; const n = !muted; activeCall.mute(n); setMuted(n); };
  const toggleHold = async () => {
    if (!callSidRef.current || holdLoading) return;
    setHoldLoading(true);
    try { const r = await fetchWithRetry(`${API_BASE}/api/call/${callSidRef.current}/hold`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ hold: !held }) }); if (r.ok) setHeld(!held); } catch { /* */ }
    setHoldLoading(false);
  };
  const setInput = (id) => { setSelectedInput(id); device?.audio?.setInputDevice(id).catch(() => {}); };
  const setOutput = (id) => { setSelectedOutput(id); device?.audio?.speakerDevices?.set(id).catch(() => {}); };
  const onKey = (e) => { if (e.key === 'Enter' && callState === CALL_STATES.idle && number.trim()) handleDial(); };

  const isIdle = callState === CALL_STATES.idle;
  const isActive = callState === CALL_STATES.active;
  const isConnecting = callState === CALL_STATES.connecting;
  const freeText = callType === 'custom';

  /* ═══ IN-CALL BAR ═══ */
  if (!isIdle) {
    return (
      <div className="dlr">
        <div className="dlr-incall">
          <div className="dlr-incall__mesh" />

          <div className="dlr-incall__left">
            <div className={`dlr-pulse ${isActive ? 'dlr-pulse--live' : 'dlr-pulse--wait'}`}><span /></div>
            <div className="dlr-incall__timer">{isActive ? fmtTime(elapsed) : 'Connecting\u2026'}</div>
            {isActive && <div className="dlr-incall__roles">{callerRole || 'Agent'} &rarr; {receiverRole || 'Customer'}</div>}
          </div>

          <div className="dlr-incall__actions">
            {isActive && (
              <>
                <button className={`dlr-action ${muted ? 'dlr-action--on' : ''}`} onClick={toggleMute}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {muted ? <><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2c0 .76-.13 1.49-.35 2.17"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></> : <><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></>}
                  </svg>
                  <span>{muted ? 'Unmute' : 'Mute'}</span>
                </button>
                <button className={`dlr-action ${held ? 'dlr-action--on' : ''}`} onClick={toggleHold} disabled={holdLoading}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    {held ? <polygon points="5 3 19 12 5 21 5 3"/> : <><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></>}
                  </svg>
                  <span>{holdLoading ? '\u2026' : held ? 'Resume' : 'Hold'}</span>
                </button>
              </>
            )}
            <button className="dlr-hangup" onClick={handleHangup}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
              <span>{isConnecting ? 'Cancel' : 'End'}</span>
            </button>
          </div>

          <div className="dlr-incall__right">
            <button className={`dlr-icon-btn ${showDevices ? 'dlr-icon-btn--on' : ''}`} onClick={() => setShowDevices(!showDevices)} title="Audio devices">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
            </button>
            {showDevices && (
              <div className="dlr-device-pop">
                <label>Mic<select value={selectedInput} onChange={(e) => setInput(e.target.value)}>{inputDevices.length === 0 && <option value="default">Default</option>}{inputDevices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Mic'}</option>)}</select></label>
                <label>Speaker<select value={selectedOutput} onChange={(e) => setOutput(e.target.value)}>{outputDevices.length === 0 && <option value="default">Default</option>}{outputDevices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Speaker'}</option>)}</select></label>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ═══ IDLE: PRE-CALL PANEL ═══ */
  return (
    <div className="dlr">
      <div className="dlr-idle">
        <div className="dlr-idle__mesh" />

        {/* ── Left: Dialpad ── */}
        <div className="dlr-dial">
          <div className="dlr-dial__input-wrap">
            <span className={`dlr-ready ${deviceReady ? 'dlr-ready--on' : ''}`} />
            <input type="tel" className="dlr-dial__input" placeholder="Enter number" value={number} onChange={(e) => setNumber(e.target.value)} onKeyDown={onKey} />
            {number && <button className="dlr-dial__clear" onClick={() => setNumber('')}>&times;</button>}
          </div>

          <div className="dlr-pad">
            {DIALPAD.map((k) => (
              <button key={k} className="dlr-pad__key" onClick={() => padPress(k)}>
                <span className="dlr-pad__digit">{k}</span>
                {DIALPAD_LETTERS[k] && <span className="dlr-pad__letters">{DIALPAD_LETTERS[k]}</span>}
              </button>
            ))}
          </div>

          <button className="dlr-call-btn" onClick={handleDial} disabled={!number.trim() || !deviceReady}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2z"/></svg>
            <span>{!deviceReady ? statusText : number.trim() ? 'Call' : 'Enter number'}</span>
          </button>

        </div>

        {/* ── Recents (next to keypad) ── */}
        <div className="dlr-recents-col">
          <div className="dlr-section-label">Recents</div>
          {recentCalls.length > 0 ? (
            <div className="dlr-recents">
              {recentCalls.slice(0, 5).map((c, i) => (
                <button key={i} className="dlr-recent" onClick={() => setNumber(c.number)}>
                  <div className="dlr-recent__avatar">{(c.name || c.number).charAt(0).toUpperCase()}</div>
                  <div className="dlr-recent__info">
                    <span className="dlr-recent__name">{c.number}</span>
                    {c.name && <span className="dlr-recent__num">{c.name}</span>}
                  </div>
                  <span className="dlr-recent__date">{new Date(c.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="dlr-recents-empty">No recent calls</div>
          )}
        </div>

        {/* ── Center: Setup ── */}
        <div className="dlr-setup">
          <div className="dlr-section-label">Call Type</div>
          <div className="dlr-pills">
            {PRESETS.map((p) => (
              <button key={p.value} className={`dlr-pill ${callType === p.value ? 'dlr-pill--on' : ''}`} onClick={() => pickPreset(p.value)}>
                {p.label}
              </button>
            ))}
          </div>

          <div className="dlr-section-label">Roles</div>
          <div className="dlr-roles">
            <label className="dlr-role">
              <span className="dlr-role__label">You</span>
              <input type="text" className="dlr-role__input" value={callerRole} onChange={(e) => setCallerRole(e.target.value)} placeholder="Agent, Mom, Coach\u2026" readOnly={!freeText} />
            </label>
            <span className="dlr-role__arrow">&rarr;</span>
            <label className="dlr-role">
              <span className="dlr-role__label">Them</span>
              <input type="text" className="dlr-role__input" value={receiverRole} onChange={(e) => setReceiverRole(e.target.value)} placeholder="Prospect, Son\u2026" readOnly={!freeText} />
            </label>
          </div>

          <textarea className="dlr-notes" value={callContext} onChange={(e) => setCallContext(e.target.value)} placeholder="Call notes &amp; talking points (optional)\u2026" rows={2} />
        </div>

        {/* ── Right: Config ── */}
        <div className="dlr-config">
          <div className="dlr-section-label">Config</div>
          <label className="dlr-field"><span>Playbook</span>
            <select className="dlr-select" value={selectedPlaybook || 'general'} onChange={(e) => onPlaybookChange?.(e.target.value)}>
              <optgroup label="Built-in">{(playbooks || ['general']).map((pb) => <option key={pb} value={pb}>{pb.charAt(0).toUpperCase() + pb.slice(1)}</option>)}</optgroup>
              {userPlaybooks?.length > 0 && <optgroup label="My Playbooks">{userPlaybooks.map((s) => <option key={`pb:${s.id}`} value={`pb:${s.id}`}>{s.name}</option>)}</optgroup>}
            </select>
          </label>
          <label className="dlr-field"><span>Mic</span>
            <select className="dlr-select" value={selectedInput} onChange={(e) => setInput(e.target.value)}>{inputDevices.length === 0 && <option value="default">Default</option>}{inputDevices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Mic'}</option>)}</select>
          </label>
          <label className="dlr-field"><span>Speaker</span>
            <select className="dlr-select" value={selectedOutput} onChange={(e) => setOutput(e.target.value)}>{outputDevices.length === 0 && <option value="default">Default</option>}{outputDevices.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || 'Speaker'}</option>)}</select>
          </label>
          <div className="dlr-compliance-toggle" title="Calls are not recorded. No recordings or transcripts are received from the server." onClick={toggleHideTranscript} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleHideTranscript(); }}>
            <span className="dlr-compliance-label">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              Compliance Mode
            </span>
            <div className={`dlr-toggle-track ${hideTranscript ? 'dlr-toggle-track--on' : ''}`}>
              <div className="dlr-toggle-thumb" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
